import React from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { zIndex, layout } from '../../../common/constants/styles/index.tsx';
import { isStickySupported } from '../../../common/utils/is-sticky-supported/index.tsx';
import {
	useBoardHasScrollLeft,
	useBoardScrollIsAtRightEdge,
} from '../../../controllers/board-scroll/index.tsx';

const shadowWidth = 7;

type BoardBorderLeftStyleProps = {
	offsetLeftPaddingByWidth?: boolean;
	isVisualRefresh?: boolean;
};

type BoardBorderRightStyleProps = {
	offsetRightPaddingByWidth?: boolean;
};

export type LeftBorderProps = {
	offsetLeftPaddingByWidth?: boolean;
};

export const LeftBorder = ({ offsetLeftPaddingByWidth }: LeftBorderProps) => {
	const [hasScrollLeft] = useBoardHasScrollLeft();

	const { isSoftwareFullTheming } = useSoftwareProjectTheming();

	return (
		<BoardBorderLeftStyle
			isShadowVisible={hasScrollLeft}
			offsetLeftPaddingByWidth={offsetLeftPaddingByWidth}
			isVisualRefresh={isVisualRefreshEnabled()}
			isThemed={isSoftwareFullTheming}
		/>
	);
};

export type RightBorderProps = {
	offsetRightPaddingByWidth?: boolean;
};

export const RightBorder = ({ offsetRightPaddingByWidth }: RightBorderProps) => {
	const [isAtRightScrollEdge] = useBoardScrollIsAtRightEdge();

	const { isSoftwareFullTheming } = useSoftwareProjectTheming();

	return (
		<BoardBorderRightStyle
			isShadowVisible={!isSoftwareFullTheming && !isAtRightScrollEdge}
			offsetRightPaddingByWidth={offsetRightPaddingByWidth}
			isVisualRefresh={isVisualRefreshEnabled()}
			isThemed={isSoftwareFullTheming}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardBorderStyle = styled.div<{
	isShadowVisible: boolean;
	isVisualRefresh: boolean;
	isThemed: boolean;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		display: !isStickySupported ? 'none' : '',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		width: ({ isVisualRefresh }) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			isVisualRefresh ? token('space.300') : `${layout.boardBorderWidth}px`,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: ({ isShadowVisible }) => (isShadowVisible ? 1 : 0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		pointerEvents: ({ isShadowVisible }) => (isShadowVisible ? 'auto' : 'none'),
		transition: 'opacity 0.2s',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: zIndex.boardBorders,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	(props) =>
		!props.isThemed && {
			backgroundColor: token('elevation.surface'),
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardBorderLeftStyle = styled(BoardBorderStyle)<BoardBorderLeftStyleProps>({
	left: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginLeft: (props: BoardBorderLeftStyleProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		props.isVisualRefresh ? token('space.negative.300') : `${-layout.boardBorderWidth}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	boxShadow: `0 ${layout.boardBorderWidth * 2}px 0 ${token('elevation.shadow.overflow.spread')}, 5px -2px ${shadowWidth}px ${token('elevation.shadow.overflow.perimeter')}`,
	clipPath: 'inset(0px -12px 0px 0)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: BoardBorderLeftStyleProps) =>
		props.offsetLeftPaddingByWidth ? `${shadowWidth}px;` : '',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardBorderRightStyle = styled(BoardBorderStyle)<BoardBorderRightStyleProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		boxShadow: `0 ${layout.boardBorderWidth * 2}px 0 ${token('elevation.shadow.overflow.spread')}, -5px -2px ${shadowWidth}px ${token('elevation.shadow.overflow.perimeter')}`,
		clipPath: 'inset(0px 0px 0px -12px)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: BoardBorderRightStyleProps) =>
		props.offsetRightPaddingByWidth
			? {
					width: `${shadowWidth}px`,
					marginLeft: `${-shadowWidth}px`,
					right: `${-shadowWidth}px`,
				}
			: {
					right: 0,
				},
);
